// product_list_v1: Product master CRUD (inline form pattern)
// Pattern follows HotGames.js (single page, toggle form, table/cards)
import React, { useState, useEffect } from 'react';
import API from '../api';
import { Package, Plus, Edit2, Trash2, Save, X, Power, Search, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';

const emptyForm = () => ({
  name: '', category: '', unit_price: '', description: '', sort_order: 0,
});

export default function Products() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all'); // all|active|inactive
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeFilter === 'active') params.append('active', 'true');
      if (activeFilter === 'inactive') params.append('active', 'false');
      if (search.trim()) params.append('search', search.trim());
      params.append('limit', '500');
      const { data } = await API.get('/api/products?' + params.toString());
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Load products error:', e);
      toast.error('โหลดสินค้าไม่สำเร็จ');
      setList([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    const t = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, search]);

  // Stats computed from list
  const stats = {
    total: list.length,
    active: list.filter(p => p.is_active).length,
    inactive: list.filter(p => !p.is_active).length,
    totalUses: list.reduce((s, p) => s + (p.use_count || 0), 0),
  };

  const handleEdit = (p) => {
    setEditId(p.id);
    setForm({
      name: p.name || '',
      category: p.category || '',
      unit_price: p.unit_price != null ? String(p.unit_price) : '',
      description: p.description || '',
      sort_order: p.sort_order || 0,
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setForm(emptyForm());
    setEditId(null);
    setShowForm(false);
  };

  const validate = () => {
    if (!form.name.trim()) return 'กรุณากรอกชื่อสินค้า';
    const price = Number(form.unit_price);
    if (!isFinite(price) || price < 0) return 'ราคาต้องเป็นตัวเลข >= 0';
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category.trim() || null,
        unit_price: Number(form.unit_price),
        description: form.description.trim() || null,
        sort_order: Number(form.sort_order) || 0,
      };

      if (editId) {
        await API.put('/api/products/' + editId, payload);
        toast.success('อัปเดตสินค้าสำเร็จ');
      } else {
        await API.post('/api/products', payload);
        toast.success('เพิ่มสินค้าสำเร็จ');
      }
      handleCancelEdit();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'บันทึกไม่สำเร็จ');
    }
    setSaving(false);
  };

  const handleToggle = async (p) => {
    try {
      await API.patch('/api/products/' + p.id + '/toggle');
      toast.success(p.is_active ? 'ปิดสินค้าแล้ว' : 'เปิดสินค้าแล้ว');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'เปลี่ยนสถานะไม่สำเร็จ');
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm('ลบสินค้า "' + p.name + '" ?\n(ถ้าเคยใช้ในบิลแล้ว ลบไม่ได้)')) return;
    try {
      await API.delete('/api/products/' + p.id);
      toast.success('ลบสินค้าสำเร็จ');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'ลบไม่สำเร็จ (อาจถูกใช้ในบิลแล้ว)');
    }
  };

  return (
    <div style={{ padding: isMobile ? '16px 12px' : '24px 28px', background:'#f8fafc', minHeight:'100%' }}>
      {/* Header */}
      <div style={{
        display:'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent:'space-between',
        gap: isMobile ? 12 : 0,
        marginBottom: isMobile ? 16 : 24,
      }}>
        <div style={{ display:'flex', alignItems:'center', gap: 10 }}>
          <Package size={isMobile ? 22 : 26} color="#0ea5e9" />
          <div>
            <h1 style={{ margin:0, fontSize: isMobile ? 18 : 22, fontWeight:700, color:'#1e293b' }}>จัดการสินค้า/บริการ</h1>
            <p style={{ margin:0, fontSize: isMobile ? 12 : 13, color:'#64748b' }}>
              คลังสินค้าสำหรับใช้ในบิล — เลือกจาก dropdown ตอนสร้างบิลได้
            </p>
          </div>
        </div>
        {!showForm && (
          <button onClick={() => { setEditId(null); setForm(emptyForm()); setShowForm(true); }}
            style={{
              display:'flex', alignItems:'center', justifyContent:'center', gap:6,
              background:'#0ea5e9', color:'#fff', border:'none', borderRadius:8,
              padding: isMobile ? '12px 18px' : '10px 18px',
              fontSize: isMobile ? 14 : 13, fontWeight:600, cursor:'pointer',
            }}>
            <Plus size={isMobile ? 16 : 15} />เพิ่มสินค้า
          </button>
        )}
      </div>

      {/* Inline Form */}
      {showForm && (
        <div style={{
          background:'#fff', borderRadius: 14,
          padding: isMobile ? 16 : 22,
          boxShadow:'0 1px 6px rgba(0,0,0,0.07)',
          marginBottom: isMobile ? 16 : 20,
        }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 14 }}>
            <h2 style={{ margin:0, fontSize: isMobile ? 14 : 16, fontWeight: 700, color:'#1e293b' }}>
              {editId ? '✏️ แก้ไขสินค้า' : '➕ เพิ่มสินค้าใหม่'}
            </h2>
            <button onClick={handleCancelEdit}
              style={{ background:'none', border:'none', cursor:'pointer', padding: 4 }}>
              <X size={20} color="#94a3b8" />
            </button>
          </div>

          <div style={{
            display:'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : '2fr 1fr 1fr 1fr',
            gap: 12, marginBottom: 12,
          }}>
            <div style={{ gridColumn: isMobile ? '1 / -1' : 'auto' }}>
              <Label>ชื่อสินค้า/บริการ *</Label>
              <input value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="เช่น ค่าบริการรายเดือน"
                style={inputStyle(isMobile)} />
            </div>
            <div>
              <Label>หมวดหมู่</Label>
              <input value={form.category}
                onChange={e => setForm({ ...form, category: e.target.value })}
                placeholder="เช่น บริการ"
                style={inputStyle(isMobile)} />
            </div>
            <div>
              <Label color="#0ea5e9">💰 ราคา/หน่วย *</Label>
              <input type="number" step="0.01" min="0"
                value={form.unit_price}
                onChange={e => setForm({ ...form, unit_price: e.target.value })}
                placeholder="0.00"
                style={{ ...inputStyle(isMobile), border:'2px solid #bae6fd', textAlign:'right' }} />
            </div>
            <div>
              <Label>ลำดับการแสดง</Label>
              <input type="number" value={form.sort_order}
                onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
                style={inputStyle(isMobile)} />
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <Label>คำอธิบาย</Label>
            <textarea value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="คำอธิบายเพิ่มเติม (ไม่บังคับ)"
              rows={2}
              style={{ ...inputStyle(isMobile), resize:'vertical', fontFamily:'inherit' }} />
          </div>

          <div style={{ display:'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button onClick={handleCancelEdit} disabled={saving}
              style={{
                padding: isMobile ? '12px 18px' : '10px 18px',
                background:'#f1f5f9', color:'#475569', border:'none', borderRadius: 8,
                fontSize: 13, fontWeight: 600, cursor:'pointer',
              }}>
              ยกเลิก
            </button>
            <button onClick={handleSave} disabled={saving}
              style={{
                padding: isMobile ? '12px 22px' : '10px 22px',
                background: saving ? '#94a3b8' : '#0ea5e9', color:'#fff',
                border:'none', borderRadius: 8, fontSize: 14, fontWeight: 700,
                cursor: saving ? 'not-allowed' : 'pointer',
                display:'inline-flex', alignItems:'center', gap: 6,
              }}>
              <Save size={15} />
              {saving ? 'กำลังบันทึก...' : (editId ? 'บันทึก' : 'เพิ่มสินค้า')}
            </button>
          </div>
        </div>
      )}

      {/* Stats Cards (4 cols desktop / 2x2 mobile) */}
      <div style={{
        display:'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: isMobile ? 10 : 14,
        marginBottom: isMobile ? 16 : 20,
      }}>
        <StatCard label="ทั้งหมด"     value={stats.total}     color="#64748b" icon="📦" />
        <StatCard label="ใช้งาน"      value={stats.active}    color="#16a34a" icon="✅" />
        <StatCard label="ปิดใช้งาน"  value={stats.inactive}  color="#94a3b8" icon="⏸️" />
        <StatCard label="ใช้ในบิลรวม" value={stats.totalUses} color="#0ea5e9" icon="🧾" />
      </div>

      {/* Filter bar */}
      <div style={{
        background:'#fff', padding: isMobile ? 12 : 14, borderRadius: 12,
        boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
        marginBottom: isMobile ? 12 : 16,
        display:'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 10, alignItems: 'stretch',
      }}>
        <div style={{ position:'relative', flex: 1 }}>
          <Search size={16} color="#94a3b8" style={{ position:'absolute', left: 12, top:'50%', transform:'translateY(-50%)' }} />
          <input
            type="text" placeholder="ค้นหา ชื่อสินค้า หรือ คำอธิบาย..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', boxSizing:'border-box',
              padding: isMobile ? '12px 14px 12px 36px' : '9px 12px 9px 36px',
              borderRadius: 8, border: '1px solid #e2e8f0',
              fontSize: isMobile ? 16 : 14, outline:'none',
            }} />
        </div>
        <select value={activeFilter} onChange={e => setActiveFilter(e.target.value)}
          style={{
            padding: isMobile ? '12px 14px' : '9px 12px',
            borderRadius: 8, border: '1px solid #e2e8f0',
            fontSize: isMobile ? 16 : 14, outline:'none',
            background:'#fff', cursor:'pointer',
            minWidth: isMobile ? '100%' : 160,
          }}>
          <option value="all">ทั้งหมด</option>
          <option value="active">ใช้งาน</option>
          <option value="inactive">ปิดใช้งาน</option>
        </select>
        <button onClick={load}
          style={{
            display:'flex', alignItems:'center', justifyContent:'center', gap:6,
            padding: isMobile ? '12px 18px' : '9px 16px',
            background:'#f1f5f9', color:'#64748b', border:'none', borderRadius: 8,
            cursor:'pointer', fontSize: isMobile ? 14 : 13, fontWeight:500,
          }}>
          <RefreshCw size={14} />รีเฟรช
        </button>
      </div>

      {/* List */}
      <div style={{ background:'#fff', borderRadius: 12, boxShadow:'0 1px 6px rgba(0,0,0,0.05)', overflow:'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign:'center', color:'#94a3b8' }}>กำลังโหลด...</div>
        ) : list.length === 0 ? (
          <div style={{ padding: 60, textAlign:'center', color:'#94a3b8' }}>
            <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.5 }}>📦</div>
            <div style={{ fontSize: 16, fontWeight: 600, color:'#64748b', marginBottom: 6 }}>
              {search || activeFilter !== 'all' ? 'ไม่พบสินค้าที่ตรงกับเงื่อนไข' : 'ยังไม่มีสินค้า'}
            </div>
            <div style={{ fontSize: 13, color:'#94a3b8' }}>
              {search || activeFilter !== 'all' ? 'ลองล้างตัวกรองดู' : 'คลิก "เพิ่มสินค้า" เพื่อเริ่มต้น'}
            </div>
          </div>
        ) : isMobile ? (
          <div>
            {list.map((p, i) => (
              <ProductCard key={p.id} p={p} isLast={i === list.length - 1}
                onEdit={handleEdit} onToggle={handleToggle} onDelete={handleDelete} />
            ))}
          </div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background:'#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <Th>ลำดับ</Th>
                  <Th>ชื่อสินค้า</Th>
                  <Th>หมวดหมู่</Th>
                  <Th align="right">ราคา/หน่วย</Th>
                  <Th align="center">ใช้ในบิล</Th>
                  <Th align="center">สถานะ</Th>
                  <Th align="center">จัดการ</Th>
                </tr>
              </thead>
              <tbody>
                {list.map(p => (
                  <tr key={p.id} style={{
                    borderBottom: '1px solid #f1f5f9',
                    opacity: p.is_active ? 1 : 0.55,
                  }}>
                    <Td>
                      <span style={{
                        background:'#f1f5f9', color:'#64748b', fontWeight: 600,
                        padding: '3px 9px', borderRadius: 6, fontSize: 11,
                      }}>#{p.sort_order || 0}</span>
                    </Td>
                    <Td>
                      <div style={{ fontWeight: 600, color:'#1e293b' }}>{p.name}</div>
                      {p.description && (
                        <div style={{ fontSize: 11, color:'#94a3b8', marginTop: 2,
                          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth: 280 }}>
                          {p.description}
                        </div>
                      )}
                    </Td>
                    <Td>
                      {p.category ? (
                        <span style={{
                          background:'#e0f2fe', color:'#0284c7', fontWeight: 500,
                          padding: '3px 9px', borderRadius: 6, fontSize: 11,
                        }}>{p.category}</span>
                      ) : <span style={{ color:'#cbd5e1' }}>—</span>}
                    </Td>
                    <Td align="right">
                      <div style={{ fontWeight: 700, color:'#0284c7' }}>
                        {Number(p.unit_price).toLocaleString('th-TH', { minimumFractionDigits: 2 })} ฿
                      </div>
                    </Td>
                    <Td align="center">
                      <span style={{
                        background: (p.use_count || 0) > 0 ? '#fef3c7' : '#f8fafc',
                        color: (p.use_count || 0) > 0 ? '#a16207' : '#94a3b8',
                        fontWeight: 600, padding: '3px 9px', borderRadius: 6, fontSize: 12,
                      }}>{p.use_count || 0}</span>
                    </Td>
                    <Td align="center">
                      <span style={{
                        background: p.is_active ? '#dcfce7' : '#f1f5f9',
                        color: p.is_active ? '#16a34a' : '#94a3b8',
                        borderRadius: 6, padding: '4px 10px',
                        fontSize: 12, fontWeight: 600,
                      }}>
                        {p.is_active ? 'ใช้งาน' : 'ปิด'}
                      </span>
                    </Td>
                    <Td align="center">
                      <div style={{ display:'inline-flex', gap: 4 }}>
                        <IconBtn title="แก้ไข" onClick={() => handleEdit(p)} color="#64748b">
                          <Edit2 size={14} />
                        </IconBtn>
                        <IconBtn title={p.is_active ? 'ปิด' : 'เปิด'} onClick={() => handleToggle(p)} color={p.is_active ? '#ef4444' : '#16a34a'}>
                          <Power size={14} />
                        </IconBtn>
                        <IconBtn title="ลบ" onClick={() => handleDelete(p)} color="#ef4444">
                          <Trash2 size={14} />
                        </IconBtn>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Count footer */}
      {!loading && list.length > 0 && (
        <div style={{ marginTop: 12, fontSize: 12, color:'#94a3b8', textAlign:'center' }}>
          แสดง {list.length} รายการ
        </div>
      )}
    </div>
  );
}

// ---------- subcomponents ----------
function ProductCard({ p, isLast, onEdit, onToggle, onDelete }) {
  return (
    <div style={{
      padding: 14,
      borderBottom: isLast ? 'none' : '1px solid #f1f5f9',
      opacity: p.is_active ? 1 : 0.55,
    }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom: 8, gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display:'flex', alignItems:'center', gap: 6, marginBottom: 4 }}>
            <span style={{
              background:'#f1f5f9', color:'#64748b', fontWeight: 600,
              padding: '2px 7px', borderRadius: 5, fontSize: 10,
            }}>#{p.sort_order || 0}</span>
            {p.category && (
              <span style={{
                background:'#e0f2fe', color:'#0284c7',
                padding: '2px 7px', borderRadius: 5, fontSize: 10, fontWeight: 500,
              }}>{p.category}</span>
            )}
          </div>
          <div style={{ fontWeight: 700, fontSize: 15, color:'#1e293b' }}>{p.name}</div>
          {p.description && (
            <div style={{ fontSize: 12, color:'#64748b', marginTop: 3, lineHeight: 1.4 }}>{p.description}</div>
          )}
        </div>
        <span style={{
          background: p.is_active ? '#dcfce7' : '#f1f5f9',
          color: p.is_active ? '#16a34a' : '#94a3b8',
          borderRadius: 6, padding: '4px 10px',
          fontSize: 11, fontWeight: 600, flexShrink: 0,
        }}>
          {p.is_active ? 'ใช้งาน' : 'ปิด'}
        </span>
      </div>

      <div style={{
        background:'#f0f9ff', padding: '10px 12px', borderRadius: 8,
        marginBottom: 10,
        display:'flex', justifyContent:'space-between', alignItems:'center',
      }}>
        <div>
          <div style={{ fontSize: 10, color:'#0c4a6e', fontWeight: 600, textTransform: 'uppercase' }}>ราคา</div>
          <div style={{ fontSize: 17, fontWeight: 700, color:'#0284c7' }}>
            {Number(p.unit_price).toLocaleString('th-TH', { minimumFractionDigits: 2 })} ฿
          </div>
        </div>
        <div style={{ textAlign:'right' }}>
          <div style={{ fontSize: 10, color:'#0c4a6e', fontWeight: 600, textTransform: 'uppercase' }}>ใช้ในบิล</div>
          <div style={{ fontSize: 14, color:'#0c4a6e', fontWeight: 600 }}>
            {p.use_count || 0} ครั้ง
          </div>
        </div>
      </div>

      <div style={{ display:'flex', gap: 6 }}>
        <button onClick={() => onEdit(p)}
          style={{
            flex: 1, padding: '10px', borderRadius: 8,
            background:'#f1f5f9', border:'none', cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center', gap: 4,
            fontSize: 13, color:'#64748b', fontWeight: 500,
          }}>
          <Edit2 size={14} />แก้ไข
        </button>
        <button onClick={() => onToggle(p)}
          style={{
            flex: 1, padding: '10px', borderRadius: 8,
            background: p.is_active ? '#fee2e2' : '#dcfce7',
            color: p.is_active ? '#dc2626' : '#16a34a',
            border:'none', cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center', gap: 4,
            fontSize: 13, fontWeight: 500,
          }}>
          <Power size={14} />{p.is_active ? 'ปิด' : 'เปิด'}
        </button>
        <button onClick={() => onDelete(p)}
          style={{
            padding: '10px 14px', borderRadius: 8,
            background:'#fff1f2', border:'none', cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center',
          }}>
          <Trash2 size={14} color="#ef4444" />
        </button>
      </div>
    </div>
  );
}

function StatCard({ label, value, color, icon }) {
  return (
    <div style={{
      background:'#fff', padding: 16, borderRadius: 12,
      boxShadow:'0 1px 6px rgba(0,0,0,0.05)',
    }}>
      <div style={{ display:'flex', alignItems:'center', gap: 6, marginBottom: 8 }}>
        <span style={{ fontSize: 16 }}>{icon}</span>
        <span style={{ fontSize: 12, fontWeight: 600, color }}>{label}</span>
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color:'#1e293b', lineHeight: 1.1 }}>
        {value}
      </div>
    </div>
  );
}

const Th = ({ children, align }) => (
  <th style={{
    padding: '12px 14px',
    textAlign: align || 'left',
    fontSize: 12, fontWeight: 600, color:'#64748b',
    textTransform: 'uppercase', letterSpacing: '0.03em',
  }}>{children}</th>
);

const Td = ({ children, align }) => (
  <td style={{
    padding: '12px 14px',
    textAlign: align || 'left',
    color:'#334155',
    verticalAlign: 'middle',
  }}>{children}</td>
);

const Label = ({ children, color }) => (
  <label style={{
    fontSize: 12, fontWeight: 600,
    color: color || '#64748b',
    display:'block', marginBottom: 4,
  }}>{children}</label>
);

const inputStyle = (isMobile) => ({
  width:'100%', boxSizing:'border-box',
  padding: isMobile ? '12px 14px' : '9px 12px',
  borderRadius: 8, border: '1px solid #e2e8f0',
  fontSize: isMobile ? 16 : 14, outline:'none',
  background:'#fff',
});

const IconBtn = ({ children, onClick, color, title }) => (
  <button onClick={onClick} title={title}
    style={{
      padding: 6, borderRadius: 6,
      background: 'transparent', border: '1px solid #e2e8f0',
      cursor:'pointer', color,
      display:'inline-flex', alignItems:'center', justifyContent:'center',
    }}>
    {children}
  </button>
);
